import GithubApi from "#infra/ext-api/github/GithubApi";
import AdvancedCodingExerciseInfoAbs from "#infra/info/AdvancedCodingExerciseInfoAbs";
import { Box, Button, Grid, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Markdown from "../Markdown";

const mdInstructions: string = `
In this exercise, we will use <<Github>> with <<VS Code for Web>>.
Please make sure you are **logged in** to <<Github>> before you start.
`;

const forkInstructionsMd: string = `
- Then, on the top right, click on <<Fork>>.
- Keep the repository name as <<cif-sandbox>>. Description is optional.
- Make sure you uncheck the box saying <<"Copy the main branch only">>!!
- Finally, click on <<Create Fork>> at the bottom.
`;

const syncForkInstructionsMd: string = `
Then, under the green <<Code>> button, click on <<Sync Fork>>, if available.
`;

const exerciseButtonInstructionsMd: string = `
The following button will open the exercise in a new tab, in
<<VS Code for Web>>.
`;

const exerciseInstructionsMd: string = `
Follow the instructions in the <<README.md>> file. If it wasn't automatically
opened when you opened the exercise in <<VS Code for Web>>, then click on the
<<README.md>> file, which is located in the tree explorer (on the left panel).

If the tree explorer is not opened, click on the <<Explorer>> button on the left
panel of <<VS Code for Web>> (the button with 2 rectangles).

To navigate to a file, locate the file in the tree, and click on it.

Make sure you commit and push your changes from time to time,
so they are not lost. To commit and push, click on the <<Source Control>>
button on the left panel of <<VS Code for Web>> (the one that
has 3 small circles and 2 lines) - if you have uncommitted changes, this button
will have a a blue circle badge indicator with a number indicating
the number of files with changes.

After each commit, you can test your changes using the testing section below.
`;

export function AdvancedCodingTaskView(props: any) {
  const [userHasRepo, setUserHasRepo] = useState(false);
  const [githubUsernameInput, setGithubUsernameInput] = React.useState("");
  const [githubUsername, setGithubUsername] = React.useState("");
  const [isFetchFinished, setIsFetchFinished] = React.useState(false);
  const [testRefreshIdx, setTestRefreshIdx] = useState(0);
  const exerciseInfo: AdvancedCodingExerciseInfoAbs = props.exerciseInfo;
  const repo: string = "cif-sandbox";
  const handleTestRefresh = () => {
    setTestRefreshIdx(testRefreshIdx + 1);
  }

  useEffect(() => {
    async function fetchMyAPI() {
      if (githubUsername) {
        const githubApi: GithubApi = new GithubApi();
        const exists: boolean =
          await githubApi.doesRepoExist(githubUsername, repo);
        setUserHasRepo(exists);
        setIsFetchFinished(true);
      }
    }

    fetchMyAPI()
  }, [githubUsername])

  const handleSubmit = () => {
    setGithubUsername(githubUsernameInput)
    setGithubUsernameInput("");
    setIsFetchFinished(false);
  }

  return (
    <Box>
      <Markdown md={mdInstructions}/>
      <Box pb={2} mb={2} sx={{borderBottom: 1}}>
        <Box mb={2} sx={{display: "flex"}}>
          <Box sx={{ mr: 1 }}>
            <TextField
              sx={{marginRight: 2}}
              size="small"
              id="github-username"
              label="Your Github Username"
              value={githubUsernameInput}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setGithubUsernameInput(event.target.value);
              }}
            />
          </Box>
          <Button sx={{height: "40px"}} variant="contained"
            size="small" color="primary"
            disabled={!githubUsernameInput}
            onClick={handleSubmit}>
            Submit
          </Button>
        </Box>
        <Box sx={{display: "flex"}}>
          <Box sx={{ mr: 1 }}>
            Using Github username:
          </Box>
          <Box sx={{ fontWeight: 'bold' }}>
            {githubUsername}
          </Box>
        </Box>
      </Box>
      {githubUsername && isFetchFinished && ! userHasRepo &&
        CloneRepoInstructions()}
      {githubUsername && isFetchFinished && userHasRepo &&
        ExerciseInstructions(exerciseInfo, githubUsername, testRefreshIdx,
          handleTestRefresh)}
    </Box>
  )
}

function CloneRepoInstructions() {
  const repo: string = "cif-sandbox";
  const branch: string = "devel";
  const githubExerciseRepoUrl: string = "https://github.com/yinonby" +
    "/" + repo + "/tree/" + branch;

  return (
    <Box>
      <Box sx={{ typography: "h6" }}>
        We detected that you haven't forked the exercise repository. If
        this is a mistake, it might be due to a network error, please
        try again in a minute.
      </Box>
      <Box sx={{ typography: "h6" }} mb={2}>
        Follow the instructions to fork it into your Github:
      </Box>
      <Box>
        <Link to={githubExerciseRepoUrl} target="_blank">
          <Button variant="contained" size="small" color="primary">
            First, click to open the source repository
          </Button>
        </Link>
        <Markdown md={forkInstructionsMd} />
      </Box>
    </Box>
  )
}

function Iframe() {
/*
  <iframe src="https://codesandbox.io/embed/pkx99t?view=preview&hidenavigation=1"
  style={{width:"100%", height: "500px", border:0, borderRadius: "4px", overflow:"hidden",}}
  title="booking-system"
  allow="accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking"
  sandbox="allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
  */
}

type CodeSandboxIFrameProps = {
  githubUsername: string,
  gitRepoName: string,
  gitBranchName: string,
  gitPath: string,
  view: string,
  hidenavigation: number,
  width: string,
  height: string,
  title: string,
}

export default function CodeSandboxIFrame(props: CodeSandboxIFrameProps) {
  const repo: string = "github/" + props.githubUsername + "/" +
    props.gitRepoName + "/tree/" + props.gitBranchName + "/" +
    props.gitPath +
    "?view=preview&hidenavigation=1";
  const src: string = "https://codesandbox.io/embed/" + repo + "/?" +
    "&fontsize=14" +
    "&theme=dark" +
    "&view=" + props.view +
    "&hidenavigation=" + props.hidenavigation;
  const style = {
    width: props.width,
    height: props.height,
    border:0,
    borderRadius: "4px",
    overflow:"hidden",
  }

  return (
    <iframe src={src}
      style={style}
      title={props.title}
      allow="accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking"
      sandbox="allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    ></iframe>
  );
}

function ExerciseInstructions(exerciseInfo: AdvancedCodingExerciseInfoAbs,
  githubUsername: string, testRefreshIdx: number, onTestRefresh: () => void) {
  const repo: string = exerciseInfo.getRepoName();
  const branch: string = "devel";
  const exerciseName = exerciseInfo.getExerciseName();
  const githubUserRepoUrl: string = "https://github.com/" + githubUsername +
    "/" + repo + "/tree/" + branch;
  const githubDevUrl: string = "https://github.dev/" + githubUsername +
    "/" + repo + "/tree/" + branch + "/src/app/";
  const sadnboxTestUrl = "https://githubbox.com/" + githubUsername +
    "/" + repo + "/tree/" + branch + "/";
  const githubSolutionUrl = "https://github.com/yinonby/cif-sandbox/tree/" +
    branch + "/src/solutions/" + exerciseName;
  const handleClick = () => {
    onTestRefresh();
  }
  const codesandboxContainerStyle = {
    borderRight: "1px solid black",
    borderRadius: "4px",
    flex: 1,
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12}>
        <Box pb={2} mb={2} sx={{borderBottom: 1}}>
          <Box mb={1} sx={{typography: "h6"}}>
            Sync your forked repository if necessary
          </Box>
          <Box mb={1}>
            Before you begin, make sure you pull the latest version of the
            exercise repository.
          </Box>
          <Box mb={1}>
            <Link to={githubUserRepoUrl} target="_blank">
              <Button variant="contained" size="small" color="primary">
                Click to open your forked repository
              </Button>
            </Link>
          </Box>
          <Box>
            <Markdown md={syncForkInstructionsMd} />
          </Box>
        </Box>

        <Box pb={2} mb={2} sx={{borderBottom: 1}}>
          <Box mb={1} sx={{typography: "h6"}}>
            Exercise
          </Box>
          <Box mb={1}>
          <Markdown md={exerciseButtonInstructionsMd} />
          </Box>
          <Box mb={1}>
            <Link to={githubDevUrl} target="_blank">
              <Button variant="contained" size="small" color="primary">
                Click to open exercise
              </Button>
            </Link>
          </Box>
          <Markdown md={exerciseInstructionsMd} />
        </Box>

        <Box pb={2} mb={2} sx={{borderBottom: 1}}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={6}>
              <Box mb={1} sx={{typography: "h6"}}>
                Test your solution
              </Box>
              <Box mb={1}>
                This is an embedded Codesandbox, displaying unit tests running on
                the code you committed.
              </Box>
              <Box mb={1}>
                The percentage in the circle indicates what percentage of the
                tests was running, and not the success rate. If one test fails, no more test are executed.
              </Box>
              <Box mb={1}>
                If all tests <span style={{color: "green"}}>pass</span>, your
                solution is correct.
              </Box>
              <Box mb={1}>
                If some tests <span style={{color: "red"}}>fail</span>,
                find the failing test (you may have
                to scroll down), fix the problem, and then come back to test.
              </Box>
              <Box mb={4}>
                <Button variant="contained" size="small" color="primary" onClick={handleClick}>
                  Click to refresh tests
                </Button>
              </Box>
              <Box mb={1}>
                If the embedded Codesandbox doesn't work, click to open it in a new tab.
              </Box>
              <Box mb={1}>
                <Link to={sadnboxTestUrl} target="_blank">
                  <Button variant="contained" size="small" color="primary">
                    Click to open Codesandbox tests in a new tab
                  </Button>
                </Link>
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Box mb={1} key={testRefreshIdx}>
                <Box sx={codesandboxContainerStyle}>
                  <CodeSandboxIFrame
                    githubUsername={githubUsername}
                    gitRepoName={repo}
                    gitBranchName={branch}
                    gitPath=""
                    view="preview"
                    hidenavigation={1}
                    width="100%"
                    height="750px"
                    title="exercise"
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box pb={2} mb={2}>
          <Box mb={1} sx={{typography: "h6"}}>
            Suggested solution
          </Box>
          <Box mb={1}>
            Click to open the suggested solution
          </Box>
          <Link to={githubSolutionUrl} target="_blank">
            <Button variant="contained" size="small" color="primary">
              Solution
            </Button>
          </Link>
        </Box>
      </Grid>
    </Grid>
  );
}
